import { useState } from 'react';
import { Zap, Check, Copy, RefreshCw, Plus, Loader2 } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import { API } from './constants';

const SEGMENTS_RAPIDE = ['Entreprise', 'Mariage', 'Seminaire', 'CE/CSE', 'Association', 'Mairie', 'Festival', 'Domaine viticole'];
const OFFRES_RAPIDE = ['Buffet cocktail', 'Repas assis', 'Paella geante', 'Barbecue/Plancha', 'Brunch', 'Traiteur sur mesure', 'Menu decouverte', 'Coffret cadeau'];
const CANAUX_RAPIDE = ['email', 'dm', 'sms', 'whatsapp'];
const SEQUENCES = ['J+0', 'J+5', 'J+12', 'J+30'];

export function CrmRapide({ prospects, onRefresh }) {
  const [form, setForm] = useState({ nom: '', entreprise: '', evenement: '', convives: '' });
  const [segment, setSegment] = useState('Entreprise');
  const [offre, setOffre] = useState('');
  const [canal, setCanal] = useState('email');
  const [sequence, setSequence] = useState('J+0');
  const [contexte, setContexte] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async (seq) => {
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/crm/generate-rapide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom: form.nom,
          entreprise: form.entreprise,
          evenement: form.evenement,
          convives: form.convives,
          segment: segment.toLowerCase(),
          offre,
          canal,
          sequence: seq || sequence,
          contexte,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setResult(data);
      setSequence(seq || sequence);
      toast.success(`Message ${seq || sequence} genere`);
    } catch (e) {
      toast.error(e.message || 'Erreur generation');
    }
    setLoading(false);
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(result.message);
    setCopied(true);
    toast.success('Copie !');
    setTimeout(() => setCopied(false), 2000);
  };

  const saveAsProspect = async () => {
    try {
      await fetch(`${API}/api/crm/prospects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom: form.entreprise || form.nom,
          contact: form.nom,
          secteur: segment.toLowerCase().replace('ce/cse', 'ce').replace('domaine viticole', 'evenement'),
          note: `${form.evenement || ''} ${form.convives ? form.convives + ' convives' : ''} ${contexte}`.trim(),
          source: 'rapide',
        }),
      });
      toast.success('Prospect sauvegarde dans le CRM');
      if (onRefresh) onRefresh();
    } catch {
      toast.error('Erreur sauvegarde');
    }
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="crm-rapide">
      <div className="text-center mb-2">
        <h2 className="text-lg font-bold flex items-center justify-center gap-2"><Zap size={18} className="text-amber-500" /> Prospection Rapide</h2>
        <p className="text-xs text-gray-400">Generez un message en 30 secondes</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Nom / Contact" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} data-testid="rapide-nom" />
        <Input placeholder="Entreprise" value={form.entreprise} onChange={e => setForm(f => ({ ...f, entreprise: e.target.value }))} data-testid="rapide-entreprise" />
        <Input placeholder="Evenement" value={form.evenement} onChange={e => setForm(f => ({ ...f, evenement: e.target.value }))} data-testid="rapide-evenement" />
        <Input placeholder="Nb convives" value={form.convives} onChange={e => setForm(f => ({ ...f, convives: e.target.value }))} data-testid="rapide-convives" />
      </div>

      <div>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider">Segment</span>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {SEGMENTS_RAPIDE.map(s => (
            <button key={s} onClick={() => setSegment(s)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-all ${segment === s ? 'bg-[#009893]/10 border-[#009893] text-[#009893] font-medium' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
              data-testid={`seg-${s}`}>{s}</button>
          ))}
        </div>
      </div>

      <div>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider">Offre</span>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {OFFRES_RAPIDE.map(o => (
            <button key={o} onClick={() => setOffre(offre === o ? '' : o)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-all ${offre === o ? 'bg-[#009893]/10 border-[#009893] text-[#009893] font-medium' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
              data-testid={`offre-${o}`}>{o}</button>
          ))}
        </div>
      </div>

      <div>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider">Canal</span>
        <div className="flex gap-2 mt-1">
          {CANAUX_RAPIDE.map(c => (
            <button key={c} onClick={() => setCanal(c)}
              className={`px-4 py-1.5 rounded-full text-xs border transition-all capitalize ${canal === c ? 'bg-[#009893]/10 border-[#009893] text-[#009893] font-medium' : 'border-gray-200 text-gray-500'}`}
              data-testid={`canal-${c}`}>{c}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {SEQUENCES.map(seq => (
          <button key={seq} onClick={() => generate(seq)} disabled={loading}
            className={`py-3 rounded-xl text-sm font-bold border-2 transition-all ${
              sequence === seq && result ? 'bg-[#009893] text-white border-[#009893]' :
              'bg-white text-[#009893] border-[#009893]/30 hover:border-[#009893] hover:bg-[#009893]/5'
            } ${loading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
            data-testid={`seq-${seq}`}>{seq}</button>
        ))}
      </div>

      <textarea
        placeholder="Contexte optionnel (notes, infos supplementaires...)"
        value={contexte} onChange={e => setContexte(e.target.value)}
        className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white resize-none h-16 focus:border-[#009893] focus:ring-1 focus:ring-[#009893]/20 outline-none"
        data-testid="rapide-contexte"
      />

      {!result && (
        <button onClick={() => generate()} disabled={loading || !form.nom}
          className="w-full py-4 rounded-xl bg-[#009893] text-white font-bold text-base flex items-center justify-center gap-2 hover:bg-[#008580] disabled:opacity-50 transition-all"
          data-testid="rapide-generate-btn">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Zap size={18} />}
          {loading ? 'Generation IA...' : 'Generer le message'}
        </button>
      )}

      {result && (
        <div className="bg-[#f8fffe] border border-[#c0e8e7] rounded-2xl p-4 space-y-3 animate-slide-up" data-testid="rapide-result">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-[#009893]">{result.sequence} \u2014 {result.canal}</span>
            <div className="flex gap-1.5">
              <button onClick={copyMessage} className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-all ${copied ? 'bg-green-500 text-white' : 'bg-[#009893] text-white hover:bg-[#008580]'}`} data-testid="rapide-copy">
                {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copie !' : 'Copier'}
              </button>
              <button onClick={() => generate()} className="px-3 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center gap-1" data-testid="rapide-regen">
                <RefreshCw size={12} /> Regenerer
              </button>
            </div>
          </div>
          <pre className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed font-sans">{result.message}</pre>
          <div className="flex gap-2">
            <button onClick={saveAsProspect} className="flex-1 py-2 rounded-lg bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200 flex items-center justify-center gap-1" data-testid="rapide-save">
              <Plus size={12} /> Sauvegarder en prospect
            </button>
            {SEQUENCES.filter(s => s !== result.sequence).map(s => (
              <button key={s} onClick={() => generate(s)} disabled={loading}
                className="px-3 py-2 rounded-lg bg-[#009893]/10 text-[#009893] text-[10px] font-medium hover:bg-[#009893]/20"
                data-testid={`also-${s}`}>{s} ?</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
